import { useEffect, useMemo, useState } from "react";
import { Car, Plus, Pencil, Trash as TrashIcon, X, Download, MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ModalPortal from "@/components/ModalPortal";

const MONTH_NAMES = [
  "Januari", "Februari", "Mars", "April", "Maj", "Juni",
  "Juli", "Augusti", "September", "Oktober", "November", "December",
];

function currentYearMonth() {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

const MODE_STORAGE_KEY = "drive_log_mode_v1";
// Två lägen:
//   "simple"      — bara datum, från, till, ärende, km (för snabb personlig loggning)
//   "skatteverket" — full loggning med fordon + mätarställning (för avdrag/revision)
function getStoredMode() {
  if (typeof window === "undefined") return "simple";
  try {
    return window.localStorage.getItem(MODE_STORAGE_KEY) || "simple";
  } catch { return "simple"; }
}
function storeMode(m) {
  try { window.localStorage.setItem(MODE_STORAGE_KEY, m); } catch { /* silent */ }
}

export default function DriveLog() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [{ year, month }, setPeriod] = useState(currentYearMonth);
  const [filterUserId, setFilterUserId] = useState("");
  const [mode, setMode] = useState(getStoredMode);
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({ total_km: 0, reimbursement_sek: 0 });
  const [users, setUsers] = useState([]);
  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | entry
  const [downloading, setDownloading] = useState(false);

  const changeMode = (m) => {
    setMode(m);
    storeMode(m);
  };

  const isSkatteverket = mode === "skatteverket";

  const yearOptions = useMemo(() => {
    const now = new Date().getFullYear();
    return [now - 2, now - 1, now, now + 1];
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = { year, month };
      if (isAdmin && filterUserId) params.user_id = filterUserId;
      const { data } = await api.get("/drive-log", { params });
      setEntries(data.entries || []);
      setTotals({
        total_km: data.total_km || 0,
        reimbursement_sek: data.reimbursement_sek || 0,
      });
    } catch (err) {
      toast.error("Kunde inte hämta körjournal");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/objects");
        setObjects(data || []);
      } catch { /* silent */ }
      if (isAdmin) {
        try {
          const { data } = await api.get("/users");
          setUsers(data || []);
        } catch { /* silent */ }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, filterUserId]);

  const openNew = () => setEditing("new");
  const openEdit = (entry) => setEditing(entry);
  const closeForm = () => setEditing(null);

  const onSaved = async () => {
    closeForm();
    await load();
  };

  const onDelete = async (entry) => {
    if (!window.confirm("Radera denna resa? Den flyttas till papperskorgen i 30 dagar.")) return;
    try {
      await api.delete(`/drive-log/${entry.entry_id}`);
      toast.success("Resan flyttad till papperskorgen");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte radera");
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const params = { year, month };
      if (isAdmin && filterUserId) params.user_id = filterUserId;
      const response = await api.get("/drive-log/export/pdf", {
        params,
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `korjournal-${year}-${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Kunde inte skapa PDF");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="drive-log-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl text-forest flex items-center gap-2">
            <Car size={28} /> Körjournal
          </h1>
          <p className="text-sm text-text-muted mt-1 max-w-2xl">
            {isSkatteverket
              ? "Fullständig loggning enligt Skatteverkets krav: datum, från/till-adress, ärende, mätarställning och fordon. Exportera månadsvis PDF till bokföringen."
              : "Enkel loggning: datum, från, till, ärende och antal km. Perfekt för snabb egen översikt. Byt till Skatteverket-läge om du behöver reg.nr och mätarställning."}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="drive-log-export-btn"
            onClick={downloadPdf}
            disabled={downloading || entries.length === 0}
            className="tk-btn-outline inline-flex items-center gap-2"
          >
            <Download size={16} />
            {downloading ? "Skapar…" : "Ladda ner PDF"}
          </button>
          <button
            data-testid="drive-log-new-btn"
            onClick={openNew}
            className="tk-btn-primary inline-flex items-center gap-2"
          >
            <Plus size={16} /> Ny resa
          </button>
        </div>
      </div>

      <div
        data-testid="drive-log-mode-toggle"
        className="inline-flex rounded-full border border-app bg-white p-1 text-sm"
      >
        <button
          data-testid="drive-log-mode-simple"
          onClick={() => changeMode("simple")}
          className={`px-4 py-1.5 rounded-full transition-colors ${
            !isSkatteverket
              ? "bg-[var(--primary,#c25934)] text-white"
              : "text-text-muted hover:text-text-primary"
          }`}
        >
          Enkel
        </button>
        <button
          data-testid="drive-log-mode-skv"
          onClick={() => changeMode("skatteverket")}
          className={`px-4 py-1.5 rounded-full transition-colors ${
            isSkatteverket
              ? "bg-[var(--primary,#c25934)] text-white"
              : "text-text-muted hover:text-text-primary"
          }`}
        >
          Skatteverket-läge
        </button>
      </div>

      <div className="tk-card p-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-xs uppercase tracking-wider text-text-muted">År</label>
          <select
            data-testid="drive-log-year"
            value={year}
            onChange={(e) => setPeriod((p) => ({ ...p, year: Number(e.target.value) }))}
            className="tk-input block mt-1"
          >
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-text-muted">Månad</label>
          <select
            data-testid="drive-log-month"
            value={month}
            onChange={(e) => setPeriod((p) => ({ ...p, month: Number(e.target.value) }))}
            className="tk-input block mt-1"
          >
            {MONTH_NAMES.map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>
        </div>
        {isAdmin && (
          <div>
            <label className="text-xs uppercase tracking-wider text-text-muted">Anställd</label>
            <select
              data-testid="drive-log-user-filter"
              value={filterUserId}
              onChange={(e) => setFilterUserId(e.target.value)}
              className="tk-input block mt-1"
            >
              <option value="">Alla</option>
              {users.map((u) => (
                <option key={u.user_id} value={u.user_id}>{u.name}</option>
              ))}
            </select>
          </div>
        )}
        <div className="flex-1" />
        <div className="text-right">
          <div className="text-xs uppercase tracking-wider text-text-muted">Totalt</div>
          <div className="font-heading text-2xl text-text-primary" data-testid="drive-log-total-km">
            {totals.total_km.toFixed(1)} km
          </div>
          <div className="text-xs text-text-muted">
            ≈ {totals.reimbursement_sek.toFixed(2)} kr (schablon 2,50 kr/km)
          </div>
        </div>
      </div>

      <div className="tk-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-text-muted">Laddar…</div>
        ) : entries.length === 0 ? (
          <div className="p-10 text-center">
            <Car size={40} className="mx-auto text-text-muted mb-3" />
            <div className="font-heading text-lg">Inga resor loggade</div>
            <p className="text-sm text-text-muted mt-1 max-w-md mx-auto">
              Tryck på <strong>Ny resa</strong> för att lägga till din första
              tjänsteresa för {MONTH_NAMES[month - 1].toLowerCase()} {year}.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface text-left text-xs uppercase tracking-wider text-text-muted">
                <tr>
                  <th className="p-3">Datum</th>
                  {isAdmin && <th className="p-3">Förare</th>}
                  <th className="p-3">Från</th>
                  <th className="p-3">Till</th>
                  <th className="p-3">Ärende</th>
                  {isSkatteverket && <th className="p-3">Fordon</th>}
                  <th className="p-3 text-right">Km</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const canEdit = isAdmin || e.user_id === user.user_id;
                  return (
                    <tr key={e.entry_id} className="border-t border-app hover:bg-surface-hover" data-testid={`drive-log-row-${e.entry_id}`}>
                      <td className="p-3 font-mono text-xs">{e.trip_date}</td>
                      {isAdmin && <td className="p-3">{e.user_name || "—"}</td>}
                      <td className="p-3 max-w-[220px]">
                        <div className="flex items-start gap-1">
                          <MapPin size={12} className="mt-1 flex-shrink-0 text-text-muted" />
                          <span className="break-words">{e.from_address}</span>
                        </div>
                      </td>
                      <td className="p-3 max-w-[220px]">
                        <div className="flex items-start gap-1">
                          <MapPin size={12} className="mt-1 flex-shrink-0 text-text-muted" />
                          <span className="break-words">{e.to_address}</span>
                        </div>
                      </td>
                      <td className="p-3 max-w-[200px] break-words">
                        {e.purpose}
                        {e.object_name && (
                          <div className="text-[11px] text-text-muted mt-0.5">Objekt: {e.object_name}</div>
                        )}
                      </td>
                      {isSkatteverket && (
                        <td className="p-3 font-mono text-xs">{e.vehicle_reg || "—"}</td>
                      )}
                      <td className="p-3 text-right font-mono">{e.distance_km.toFixed(1)}</td>
                      <td className="p-3 text-right whitespace-nowrap">
                        {canEdit && (
                          <>
                            <button
                              data-testid={`drive-log-edit-${e.entry_id}`}
                              onClick={() => openEdit(e)}
                              className="p-1.5 text-text-muted hover:text-text-primary"
                              title="Redigera"
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              data-testid={`drive-log-delete-${e.entry_id}`}
                              onClick={() => onDelete(e)}
                              className="p-1.5 text-text-muted hover:text-red-600"
                              title="Radera"
                            >
                              <TrashIcon size={16} />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <DriveEntryForm
          entry={editing === "new" ? null : editing}
          objects={objects}
          isSkatteverket={isSkatteverket}
          onClose={closeForm}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

function DriveEntryForm({ entry, objects, isSkatteverket, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    trip_date: entry?.trip_date || new Date().toISOString().slice(0, 10),
    from_address: entry?.from_address || "",
    to_address: entry?.to_address || "",
    purpose: entry?.purpose || "",
    distance_km: entry?.distance_km ?? "",
    odometer_start: entry?.odometer_start ?? "",
    odometer_end: entry?.odometer_end ?? "",
    vehicle_reg: entry?.vehicle_reg || "",
    object_id: entry?.object_id || "",
    note: entry?.note || "",
  }));
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Om båda mätarställningar är angivna — beräkna distans automatiskt
  useEffect(() => {
    const s = parseFloat(form.odometer_start);
    const e = parseFloat(form.odometer_end);
    if (!isNaN(s) && !isNaN(e) && e > s) {
      set("distance_km", (e - s).toFixed(1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.odometer_start, form.odometer_end]);

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    if (saving) return;
    const km = parseFloat(form.distance_km);
    if (!km || km <= 0) {
      toast.error("Ange antal km");
      return;
    }
    if (!form.from_address.trim() || !form.to_address.trim()) {
      toast.error("Från- och Till-adress krävs");
      return;
    }
    if (!form.purpose.trim()) {
      toast.error("Ärende/syfte krävs");
      return;
    }
    // vehicle_reg är obligatoriskt endast i Skatteverket-läge (Skatteverkets krav)
    if (isSkatteverket && !form.vehicle_reg.trim()) {
      toast.error("Fordonets registreringsnummer krävs för Skatteverket-läget");
      return;
    }

    const payload = {
      trip_date: form.trip_date,
      from_address: form.from_address,
      to_address: form.to_address,
      purpose: form.purpose,
      distance_km: km,
      vehicle_reg: form.vehicle_reg,
      odometer_start: form.odometer_start !== "" ? parseFloat(form.odometer_start) : null,
      odometer_end: form.odometer_end !== "" ? parseFloat(form.odometer_end) : null,
      object_id: form.object_id || null,
      note: form.note || null,
    };

    setSaving(true);
    try {
      if (entry) {
        await api.put(`/drive-log/${entry.entry_id}`, payload);
        toast.success("Resa uppdaterad");
      } else {
        await api.post("/drive-log", payload);
        toast.success("Resa sparad");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte spara");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalPortal>
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="drive-log-form-modal"
      >
        <div className="flex items-center justify-between p-5 border-b border-app">
          <h2 className="font-heading text-2xl">
            {entry ? "Redigera resa" : "Ny resa"}
          </h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-surface-hover">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-text-muted">Datum</label>
              <input
                data-testid="drive-log-input-date"
                type="date"
                required
                value={form.trip_date}
                onChange={(e) => set("trip_date", e.target.value)}
                className="tk-input w-full mt-1"
              />
            </div>
            {isSkatteverket && (
              <div>
                <label className="text-xs uppercase tracking-wider text-text-muted">Reg.nr *</label>
                <input
                  data-testid="drive-log-input-vehicle"
                  type="text"
                  required
                  maxLength={10}
                  placeholder="ABC123"
                  value={form.vehicle_reg}
                  onChange={(e) => set("vehicle_reg", e.target.value)}
                  className="tk-input w-full mt-1 uppercase"
                />
              </div>
            )}
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-text-muted">Från *</label>
            <input
              data-testid="drive-log-input-from"
              type="text"
              required
              placeholder="T.ex. Kontoret, Storgatan 1, Stockholm"
              value={form.from_address}
              onChange={(e) => set("from_address", e.target.value)}
              className="tk-input w-full mt-1"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-text-muted">Till *</label>
            <input
              data-testid="drive-log-input-to"
              type="text"
              required
              placeholder="T.ex. Kund, Sveavägen 100, Stockholm"
              value={form.to_address}
              onChange={(e) => set("to_address", e.target.value)}
              className="tk-input w-full mt-1"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-text-muted">Ärende / syfte *</label>
            <input
              data-testid="drive-log-input-purpose"
              type="text"
              required
              placeholder="T.ex. Möte om trapprenovering"
              value={form.purpose}
              onChange={(e) => set("purpose", e.target.value)}
              className="tk-input w-full mt-1"
            />
          </div>

          <div className={`grid grid-cols-1 ${isSkatteverket ? "sm:grid-cols-3" : ""} gap-4`}>
            {isSkatteverket && (
              <>
                <div>
                  <label className="text-xs uppercase tracking-wider text-text-muted">Mätare start</label>
                  <input
                    data-testid="drive-log-input-odo-start"
                    type="number"
                    step="0.1"
                    min="0"
                    placeholder="45012"
                    value={form.odometer_start}
                    onChange={(e) => set("odometer_start", e.target.value)}
                    className="tk-input w-full mt-1"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-text-muted">Mätare slut</label>
                  <input
                    data-testid="drive-log-input-odo-end"
                    type="number"
                    step="0.1"
                    min="0"
                    placeholder="45024.5"
                    value={form.odometer_end}
                    onChange={(e) => set("odometer_end", e.target.value)}
                    className="tk-input w-full mt-1"
                  />
                </div>
              </>
            )}
            <div>
              <label className="text-xs uppercase tracking-wider text-text-muted">
                Antal km * {isSkatteverket && form.odometer_start && form.odometer_end && (
                  <span className="text-[10px] text-[var(--primary,#c25934)]">(beräknas)</span>
                )}
              </label>
              <input
                data-testid="drive-log-input-km"
                type="number"
                step="0.1"
                min="0.1"
                required
                value={form.distance_km}
                onChange={(e) => set("distance_km", e.target.value)}
                className="tk-input w-full mt-1"
              />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-text-muted">Objekt (valfritt)</label>
            <select
              data-testid="drive-log-input-object"
              value={form.object_id}
              onChange={(e) => set("object_id", e.target.value)}
              className="tk-input w-full mt-1"
            >
              <option value="">— Ingen koppling —</option>
              {objects.map((o) => (
                <option key={o.object_id} value={o.object_id}>{o.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-text-muted">Anteckning</label>
            <textarea
              data-testid="drive-log-input-note"
              rows={2}
              maxLength={500}
              placeholder="Extra info..."
              value={form.note}
              onChange={(e) => set("note", e.target.value)}
              className="tk-input w-full mt-1 resize-none"
            />
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <button type="button" onClick={onClose} className="tk-btn-outline">Avbryt</button>
            <button
              type="submit"
              data-testid="drive-log-save-btn"
              disabled={saving}
              className="tk-btn-primary"
            >
              {saving ? "Sparar…" : entry ? "Uppdatera" : "Spara resa"}
            </button>
          </div>
        </form>
      </div>
    </div>
    </ModalPortal>
  );
}
